import { z } from "zod";
export const DISCORD_SNOWFLAKE_PATTERN = /^[1-9][0-9]{0,19}$/;
export const DISCORD_SNOWFLAKE_MAX = 18446744073709551615n;
export const discordSnowflakeSchema = z
    .string()
    .regex(DISCORD_SNOWFLAKE_PATTERN, "Invalid Discord snowflake")
    .refine((value) => DISCORD_SNOWFLAKE_PATTERN.test(value) &&
    BigInt(value) <= DISCORD_SNOWFLAKE_MAX, "Discord snowflake exceeds the unsigned 64-bit range");
export const utcTimestampSchema = z.iso
    .datetime({ offset: false })
    .refine((value) => value.endsWith("Z"), "Timestamp must be UTC");
export const allowedParentChannelIdsSchema = z
    .array(discordSnowflakeSchema)
    .transform(normalizeAllowedParentChannelIds);
const validDateSchema = z
    .date()
    .refine((value) => Number.isFinite(value.getTime()), {
    message: "Invalid date",
});
export const guildInstallationSynchronizationSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    guildName: z.string().trim().min(1).max(100).optional(),
    guildIcon: z.string().trim().min(1).max(128).nullable().optional(),
    joinedAt: validDateSchema,
    synchronizedAt: validDateSchema,
})
    .refine((value) => value.synchronizedAt >= value.joinedAt, {
    message: "Synchronization date must not precede join date",
    path: ["synchronizedAt"],
});
export const guildInstallationRemovalSchema = z.object({
    guildId: discordSnowflakeSchema,
    leftAt: validDateSchema,
});
export const guildChannelSynchronizationSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    channelId: discordSnowflakeSchema,
    channelName: z.string().trim().min(1).max(100),
    channelType: z.int().min(0).max(255),
    parentId: discordSnowflakeSchema.nullable().optional(),
    position: z.int().nonnegative(),
    synchronizedAt: validDateSchema,
})
    .refine((value) => value.parentId !== value.channelId, {
    message: "Parent channel must differ from channel",
    path: ["parentId"],
});
export const guildChannelSnapshotSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    channels: z.array(guildChannelSynchronizationSchema),
    synchronizedAt: validDateSchema,
})
    .refine((value) => value.channels.every((channel) => channel.guildId === value.guildId), {
    message: "Snapshot channels must belong to the snapshot guild",
    path: ["channels"],
});
export const guildChannelInactivationSchema = z.object({
    guildId: discordSnowflakeSchema,
    channelId: discordSnowflakeSchema,
    synchronizedAt: validDateSchema,
});
export const settingsActorSchema = z.object({
    source: z.enum(["discord", "web", "system"]),
    userId: discordSnowflakeSchema.optional(),
});
export const guildSettingsUpdateSchema = z.object({
    guildId: discordSnowflakeSchema,
    mentionsEnabled: z.boolean(),
    allowedParentChannelIds: allowedParentChannelIdsSchema,
    expectedRevision: z.int().positive(),
    actor: settingsActorSchema,
    action: z.string().trim().min(1).max(100),
});
export const guildSettingsSchema = z.object({
    guildId: discordSnowflakeSchema,
    mentionsEnabled: z.boolean(),
    allowedParentChannelIds: allowedParentChannelIdsSchema,
    revision: z.int().positive(),
    createdAt: utcTimestampSchema,
    updatedAt: utcTimestampSchema,
});
export function normalizeAllowedParentChannelIds(values) {
    return [...new Set(values)].sort((left, right) => {
        const lengthDifference = left.length - right.length;
        return lengthDifference === 0
            ? left.localeCompare(right)
            : lengthDifference;
    });
}
//# sourceMappingURL=guild-settings.js.map